import { ActionSheet, ActionSheetButtonStyle } from "@capacitor/action-sheet"
import { Dialog } from "@capacitor/dialog"
import {
	isConcurrentCallError,
	popup,
	type ShowPopupOptions,
} from "@telegram-apps/sdk-react"
import * as m from "@/paraglide/messages"
import { detectPlatform } from "./detect"

type ButtonType = "default" | "ok" | "close" | "cancel" | "destructive"

export interface PopupButton {
	id: string
	text: string
	type?: ButtonType
}

export interface PopupParams {
	title?: string
	message: string
	buttons: PopupButton[]
}

function findCancelButton(buttons: PopupButton[]): PopupButton | undefined {
	return buttons.find(
		(b) => b.type === "cancel" || b.type === "close" || b.id === "cancel",
	)
}

// Timestamp rather than a boolean: a suspended WebView can leave the native
// promise unsettled, so the lock has to expire on its own.
let popupOpenedAt = 0
const POPUP_LOCK_TTL_MS = 60_000

export function isSystemPopupOpen(): boolean {
	return Date.now() - popupOpenedAt < POPUP_LOCK_TTL_MS
}

/**
 * Кроссплатформенный попап:
 * - Telegram: нативный попап через SDK
 * - Android/iOS (Capacitor): нативные Dialog / ActionSheet
 * - Browser: window.alert / window.confirm fallback
 *
 * Конкурентный вызов гасим, а не ставим в очередь: отложенный попап всплыл бы
 * уже на другом экране, а вызывающий код и так трактует null как «выбора не было».
 *
 * @returns id нажатой кнопки, либо null если закрыли без выбора, либо попап уже
 * открыт — это состояние заранее проверяется через isSystemPopupOpen
 */
export async function showSystemPopup(
	params: PopupParams,
): Promise<string | null> {
	if (isSystemPopupOpen()) return null
	popupOpenedAt = Date.now()
	try {
		const platform = detectPlatform()
		// show.isAvailable() over popup.isSupported(): the latter only covers the
		// Mini Apps version, not an uninitialised SDK or a non-Mini-App environment.
		if (platform === "telegram" && popup.show.isAvailable()) {
			return await showTelegramPopup(params)
		}
		if (platform === "android" || platform === "ios") {
			return await showCapacitorPopup(params)
		}
		return showBrowserFallback(params)
	} finally {
		popupOpenedAt = 0
	}
}

function withTitle(
	title: string | undefined,
	message: string,
	separator: string,
): string {
	if (!title) return message
	return `${title}${separator}${message}`
}

function soleButton(buttons: PopupButton[]): PopupButton | undefined {
	if (buttons.length !== 1) return undefined
	return buttons[0]
}

// The action button falls back to the first one when every button is a cancel.
function confirmButtons(
	buttons: PopupButton[],
): { action: PopupButton; cancel: PopupButton | undefined } | null {
	const cancel = findCancelButton(buttons)
	const action = buttons.find((b) => b !== cancel) ?? buttons[0]
	if (!action) return null
	return { action, cancel }
}

function actionSheetStyle(
	type: ButtonType | undefined,
): ActionSheetButtonStyle {
	if (type === "destructive") return ActionSheetButtonStyle.Destructive
	if (type === "cancel" || type === "close")
		return ActionSheetButtonStyle.Cancel
	return ActionSheetButtonStyle.Default
}

// Swallows ConcurrentCallError: popups opened outside this module bypass the
// lock above, and a duplicate show is not a failure worth reporting.
async function showTelegramPopup({
	title,
	message,
	buttons,
}: PopupParams): Promise<string | null> {
	const options: ShowPopupOptions = {
		message,
		buttons: buttons.map((btn) => ({
			id: btn.id,
			type: btn.type ?? "default",
			text: btn.text,
		})),
	}
	if (title !== undefined) options.title = title
	try {
		return await popup.show(options)
	} catch (error) {
		if (isConcurrentCallError(error)) return null
		throw error
	}
}

async function showCapacitorPopup({
	title,
	message,
	buttons,
}: PopupParams): Promise<string | null> {
	// 1 кнопка → alert
	const sole = soleButton(buttons)
	if (sole) {
		await Dialog.alert({
			title: title ?? "",
			message,
			buttonTitle: sole.text,
		})
		return sole.id
	}

	// 2 кнопки → confirm
	if (buttons.length === 2) {
		const pair = confirmButtons(buttons)
		if (pair) return showCapacitorConfirm(title, message, pair)
	}

	// 3+ кнопки → ActionSheet. Тап снаружи и «назад» на Android закрывают его
	// как кнопка отмены.
	const result = await ActionSheet.showActions({
		cancelable: true,
		title: withTitle(title, message, "\n"),
		options: buttons.map((btn) => ({
			title: btn.text,
			style: actionSheetStyle(btn.type),
		})),
	})

	// index -1: closed outside any option. A cancel-style button reports its
	// own index, so only the outside tap is mapped to it here.
	if (result.index < 0) return findCancelButton(buttons)?.id ?? null
	return buttons[result.index]?.id ?? null
}

async function showCapacitorConfirm(
	title: string | undefined,
	message: string,
	pair: { action: PopupButton; cancel: PopupButton | undefined },
): Promise<string | null> {
	const { value } = await Dialog.confirm({
		title: title ?? "",
		message,
		okButtonTitle: pair.action.text,
		cancelButtonTitle: pair.cancel?.text ?? m.commonBtnCancel(),
	})
	if (value) return pair.action.id
	return pair.cancel?.id ?? null
}

function showBrowserFallback({
	title,
	message,
	buttons,
}: PopupParams): string | null {
	const fullMessage = withTitle(title, message, "\n\n")
	const sole = soleButton(buttons)
	if (sole) {
		window.alert(fullMessage)
		return sole.id
	}

	const confirmed = window.confirm(fullMessage)
	const pair = confirmButtons(buttons)
	if (!pair) return null
	if (confirmed) return pair.action.id
	return pair.cancel?.id ?? null
}
